import { SquareMenuIcon } from "lucide-react";
import { useState } from "react";
import Joyride, { CallBackProps, STATUS, Step } from "react-joyride";
import { LemonadeProvider } from "./LemonadeProvider";
import { CumulativeStatsChart } from "./components/CumulativeStatsChart";
import { DailyStatsChart } from "./components/DailyStatsChart";
import { InventoryList } from "./components/InventoryList";
import { LemonadeList } from "./components/LemonadeList";
import { NewLemonadeForm } from "./forms/NewLemonadeForm";
import { NewPurchaseForm } from "./forms/NewPurchaseForm";
import { NewSaleForm } from "./forms/NewSaleForm";
import "./styles/AppLayout.css";
import { db } from "./internal/db";
import { SaleHistory } from "./components/SaleHistory";
import { PurchaseHistory } from "./components/PurchaseHistory";

export default function App() {
    const [page, setPage] = useState<"home" | "lemonades" | "purchases" | "history">("home");
    const [navOpen, setNavOpen] = useState(false);

    // separate run and steps for each layout
    const [joyrideRun, setJoyrideRun] = useState(false);
    const [joyrideSteps, setJoyrideSteps] = useState<Step[]>([]);

    // steps for each layout
    const homeSteps: Step[] = [
        {
            target: ".sidebar",
            content: "Ez a navigációs sáv, itt választhatsz oldalt.",
            disableBeacon: true
        },
        {
            target: ".home-btn",
            content: "Itt tudsz visszatérni a főoldalra.",
            disableBeacon: true
        },
        {
            target: ".lemonades-btn",
            content: "Itt láthatod az elérhető limonádékat, hozhatsz létre újat és adhatsz el.",
            disableBeacon: true
        },
        {
            target: ".purchases-btn",
            content: "Itt láthatod a beszerzett alapanyagokat és rögzíthetsz új beszerzést.",
            disableBeacon: true
        },
        {
            target: ".history-btn",
            content: "Itt láthatod a limonádé eladásokat és a beszerzéseket időrendi sorrendben.",
            disableBeacon: true
        },
        {
            target: ".tutorial-btn",
            content: "Itt indíthatod el a súgót.",
            disableBeacon: true
        },
        {
            target: "#daily-stats-chart",
            content: "Itt láthatod a napi statisztikákat.",
            disableBeacon: true
        },
        {
            target: "#cumulative-stats-chart",
            content: "Itt láthatod az összesített statisztikákat.",
            disableBeacon: true
        }
    ];
    const lemonadesSteps: Step[] = [
        {
            target: ".lemonades-list-area",
            content: "Itt láthatod az elérhető limonádékat.",
            disableBeacon: true
        },
        {
            target: ".lemonades-form-area",
            content: "Itt tudsz új limonádét létrehozni.",
            disableBeacon: true
        },
        {
            target: ".lemonades-sale-area",
            content: "Itt tudsz eladást rögzíteni.",
            disableBeacon: true
        }
    ];
    const purchasesSteps: Step[] = [
        {
            target: ".purchases-list-area",
            content: "Itt láthatod a beszerzett alapanyagokat.",
            disableBeacon: true
        },
        {
            target: ".purchases-form-area",
            content: "Itt tudsz új beszerzést rögzíteni.",
            disableBeacon: true
        }
    ];
    const historySteps: Step[] = [
        {
            target: ".sale-history",
            content: "Itt láthatod az eladásokat időrendi sorrendben.",
            disableBeacon: true
        },
        {
            target: ".purchase-history",
            content: "Itt láthatod a beszerzéseket időrendi sorrendben.",
            disableBeacon: true
        }
    ];

    // handler to start the tutorial for the current page
    const startTutorial = () => {
        if (page === "home") setJoyrideSteps(homeSteps);
        else if (page === "lemonades") setJoyrideSteps(lemonadesSteps);
        else if (page === "purchases") setJoyrideSteps(purchasesSteps);
        else if (page === "history") setJoyrideSteps(historySteps);
        else return;
        setJoyrideRun(true);
    };

    // Joyride callback to stop on finish/skip
    const handleJoyrideCallback = (data: CallBackProps) => {
        if (data.status === STATUS.FINISHED || data.status === STATUS.SKIPPED) {
            setJoyrideRun(false);
        }
    };

    return (
        <LemonadeProvider>
            <div className="app-layout">
                <nav className={`sidebar ${navOpen ? "open" : ""}`}>
                    <button className={`home-btn ${page === "home" ? "active" : ""}`} onClick={() => setPage("home")}>
                        Főoldal
                    </button>
                    <button className={`lemonades-btn ${page === "lemonades" ? "active" : ""}`} onClick={() => setPage("lemonades")}>
                        Limonádék
                    </button>
                    <button className={`purchases-btn ${page === "purchases" ? "active" : ""}`} onClick={() => setPage("purchases")}>
                        Készlet
                    </button>
                    <button className={`history-btn ${page === "history" ? "active" : ""}`} onClick={() => setPage("history")}>
                        Napló
                    </button>
                    <button className="tutorial-btn" onClick={startTutorial}>
                        Súgó
                    </button>
                    <button
                        className="delete-btn"
                        onClick={() => {
                            if (confirm("Biztosan törlöd az összes adatot? Ez nem visszavonható!")) {
                                db.delete().then(() => {
                                    window.location.reload();
                                });
                            }
                        }}
                    >
                        Minden törlése
                    </button>
                </nav>
                <button className="hamburger" aria-label="Menü" onClick={() => setNavOpen((v) => !v)}>
                    <SquareMenuIcon width={"2.5rem"} height={"2.5rem"} />
                </button>
                <main className="main-content">
                    {page === "home" && (
                        <div>
                            <DailyStatsChart />
                            <CumulativeStatsChart />
                        </div>
                    )}
                    {page === "lemonades" && (
                        <div className="lemonades-layout">
                            <div className="lemonades-list-area">
                                <LemonadeList />
                            </div>
                            <div className="lemonades-form-area">
                                <NewLemonadeForm />
                            </div>
                            <div className="lemonades-sale-area">
                                <NewSaleForm />
                            </div>
                        </div>
                    )}
                    {page === "purchases" && (
                        <div className="purchases-flex-layout">
                            <div className="purchases-list-area">
                                <InventoryList />
                            </div>
                            <div className="purchases-form-area">
                                <NewPurchaseForm />
                            </div>
                        </div>
                    )}
                    {page === "history" && (
                        <div className="history-layout">
                            <SaleHistory />
                            <PurchaseHistory />
                        </div>
                    )}
                </main>
                <Joyride
                    steps={joyrideSteps}
                    run={joyrideRun}
                    continuous
                    showSkipButton
                    showProgress
                    locale={{
                        back: "Vissza",
                        close: "Bezárás",
                        last: "Befejezés",
                        next: "Következő",
                        skip: "Kihagyás",
                        nextLabelWithProgress: "Következő ({step} / {steps})"
                    }}
                    styles={{
                        options: {
                            zIndex: 10000,
                            primaryColor: "#339a2c",
                            backgroundColor: "#3d5236",
                            textColor: "#faf3dd"
                        }
                    }}
                    callback={handleJoyrideCallback}
                />
            </div>
        </LemonadeProvider>
    );
}
